import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Tooltip } from '@mui/material';
import { fetchFleetProgress } from './nodeClaimsApi';
import type { FleetClaimProgress } from './types';

const Stat = ({ label, value, hint }: { label: string; value: number; hint?: string }) => {
    const tile = (
        <Card variant="outlined" sx={{ minWidth: 150, flex: '1 1 150px' }}>
            <CardContent sx={{ py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>{value}</Typography>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
            </CardContent>
        </Card>
    );
    return hint ? <Tooltip title={hint}>{tile}</Tooltip> : tile;
};

/**
 * Fleet claim progress, counted per node by its newest authorization.
 *
 * "Nodes with no authorization" is deliberately NOT shown. The server always returns 0 for it
 * — GetFleetClaimProgressQueryHandler has no way to enumerate known nodes — and a tile reading
 * a permanent 0 would be read as "none missing", which is the one conclusion the number cannot
 * support.
 */
export const FleetProgress = () => {
    const [progress, setProgress] = useState<FleetClaimProgress | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetchFleetProgress()
            .then(data => { if (!cancelled) setProgress(data); })
            .catch((e: Error) => { if (!cancelled) setError(e.message); });
        return () => { cancelled = true; };
    }, []);

    if (error) {
        return (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Fleet progress unavailable: {error}
            </Typography>
        );
    }

    if (!progress) return null;

    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary">Fleet claim progress</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                <Stat label="Claimed" value={progress.claimed} hint="Nodes running on a key issued through a claim." />
                <Stat
                    label="Armed, not claimed"
                    value={progress.armedNotClaimed}
                    hint="The queue of cards in flight. These are waiting for hardware to be fitted and powered on."
                />
                <Stat label="Expired" value={progress.expired} hint="Armed, never claimed, window lapsed. Re-arm to retry." />
                <Stat label="Revoked" value={progress.revoked} hint="Authorization withdrawn by an operator." />
            </Box>
        </Box>
    );
};
